import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import ContentCard from '@/components/base/ContentCard';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import type { ContentItem } from '@/types/content';
import { filterBySubCategory, getCategoryMeta, getItemsByCategory, sortByTime } from '@/utils/content';
import FilterBar from '@/pages/category/components/FilterBar';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryName = '' } = useParams();
  const meta = getCategoryMeta(categoryName);

  const [sub, setSub] = useState('全部');
  const [sort, setSort] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setSub('全部');
    setSort('desc');
    setPage(1);
  }, [categoryName]);

  useEffect(() => {
    setPage(1);
  }, [sub, sort]);

  useDocumentMeta({
    title: meta ? `【${meta.name}】频道 - 91网站` : '频道 - 91网站',
    description: meta
      ? `${meta.description} 91网站${meta.name}频道为你提供优质的${meta.name}内容与便捷服务。`
      : '91网站综合门户频道页。',
    keywords: meta ? meta.keywords : '91网站,综合门户,频道',
  });

  const list = useMemo<ContentItem[]>(() => {
    if (!meta) return [];
    const base = getItemsByCategory(categoryName);
    return sortByTime(filterBySubCategory(base, sub), sort);
  }, [categoryName, sub, sort, meta]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageItems = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!meta) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <i className="ri-emotion-sad-line text-5xl text-secondary-300"></i>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">该频道不存在</h1>
          <p className="mt-2 text-sm text-secondary-500">你访问的分类频道不存在或已调整，请返回首页浏览其他内容。</p>
          <Link
            to="/"
            className="mt-6 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb items={[{ label: `${meta.name}频道` }]} />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${meta.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{meta.name}频道</h1>
              <p className="mt-1 text-xs text-accent-600 md:text-sm">{meta.tagline}</p>
            </div>
          </div>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-secondary-600">{meta.description}</p>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 md:py-8">
        <FilterBar
          subCategories={meta.subCategories}
          activeSub={sub}
          onSubChange={setSub}
          sort={sort}
          onSortChange={setSort}
        />

        <div className="mt-4 flex items-center justify-between text-xs text-secondary-500">
          <span>
            共 <strong className="text-foreground-800">{list.length}</strong> 条内容
            {sub !== '全部' && ` · 子分类「${sub}」`}
          </span>
          <span className="hidden sm:inline">
            第 {page} / {totalPages} 页
          </span>
        </div>

        {pageItems.length > 0 ? (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-secondary-300"></i>
            <p className="mt-3 text-sm text-secondary-500">该筛选条件下暂无内容，试试其他子分类吧</p>
            <button
              type="button"
              onClick={() => setSub('全部')}
              className="mt-4 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              查看全部
            </button>
          </div>
        )}

        <Pagination current={page} total={totalPages} onChange={setPage} />
      </div>
    </SiteLayout>
  );
}